import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { AbstractControl } from '@angular/forms';
import { AuthService, AuthUserInfo } from '@app/services/auth.service';
import { TextsService } from '@app/services/texts.service';
import { AUTOFILL_INTENT_KEY, PROFILE_FIELD_MAP } from '@app/helpers/profile-prefill';

const AUTOFILL_MESSAGE_CLOSE_TIMEOUT_MS = 5000;

type OptionKey = string | number;
type FieldOption = { key: OptionKey; label: string };

/**
 * The bits of the host WebformComponent the autofill logic needs. Handed over via
 * attach() once the form is built (and again on confirmation reset). `messages` stays
 * owned by the component (it is shared with submit/info messages), so the service writes
 * to it through addMessage and triggers change detection through detectChanges.
 */
export interface FormAutofillContext {
  webformId: string;
  getControl: (id: string) => AbstractControl | null;
  getField: (id: string) => Record<string, unknown> | undefined;
  addMessage: (msg: {
    id: string;
    level: string;
    text: string;
    messageCloseTimeout?: number;
  }) => void;
  detectChanges: () => void;
}

/**
 * Per-form Mobiliar ID autofill. Owns the prefill engine, the post-login intent flow and
 * the banner channel. Provided in WebformComponent `providers` (NOT root) so each rendered
 * form gets its own instance and `filled` resets per form. The in-form login banner talks
 * only to this service (requestFill / startLogin / authEnabled / isAuthenticated / filled).
 */
@Injectable()
export class FormAutofillService {
  readonly #auth = inject(AuthService);
  readonly #texts = inject(TextsService);
  readonly #platformId = inject<Object>(PLATFORM_ID);

  // re-exposed so the banner depends only on this service
  readonly authEnabled = computed(() => this.#auth.isEnabled());
  readonly isAuthenticated = this.#auth.isAuthenticated;
  readonly #filled = signal(false);
  readonly filled = this.#filled.asReadonly();
  readonly #loginControlUsed = signal(false);
  readonly loginControlUsed = this.#loginControlUsed.asReadonly();

  #ctx: FormAutofillContext | null = null;
  #intentActive = false;
  #notified = false;
  #urlAutofillRequested = false;

  constructor() {
    // After a login started from the in-form "Anmelden und automatisch ausfüllen" button,
    // auto-fill once the profile resolves. The userinfo request is async, so the profile
    // may arrive (and enrich) after the form is built. No-ops until attached. The effect is
    // owned by the component injector, so it is cleaned up when the form is destroyed.
    effect(() => {
      this.#auth.userInfo();
      this.#auth.isAuthenticated();
      this.#maybeAutofillAfterLogin();
    });
  }

  /**
   * Wire the service to a built form. Resets per-form state (so the banner state does not
   * leak across a confirmation reset, which re-runs ngOnInit on the same instance) and runs
   * the initial post-login fill.
   */
  attach(ctx: FormAutofillContext): void {
    this.#ctx = ctx;
    this.#filled.set(false);
    this.#loginControlUsed.set(false);
    this.#intentActive = false;
    this.#notified = false;
    this.#maybeAutofillAfterLogin();
    if (this.#urlAutofillRequested) this.#startUrlAutofill();
  }

  /**
   * Configure the opt-in external-app flow before the form is rendered. A valid
   * URL trigger starts the same intent flow the login card uses, including a
   * login redirect when necessary.
   */
  configureUrlAutofill(requested: boolean): void {
    this.#urlAutofillRequested = requested;
  }

  /**
   * Triggered by the in-form banner when a signed-in user clicks "Automatisch ausfüllen".
   * Explicit user action -> force, so the re-fill state re-applies the profile even over
   * values the user has already edited.
   */
  requestFill(): void {
    if (!this.#ctx) return;
    this.#loginControlUsed.set(true);
    const info: AuthUserInfo | null = this.#auth.userInfo();
    if (!info || !this.isAuthenticated()) {
      this.#pushMessage('error');
      this.#ctx.detectChanges();
      return;
    }

    const patched = this.applyProfilePrefill(true);
    if (patched > 0) {
      this.#filled.set(true);
      this.#pushMessage('success');
    } else {
      this.#pushMessage('error');
    }
    this.#ctx.detectChanges();
  }

  /**
   * Logged-out banner flow: remember that the user wants to auto-fill after login, then
   * redirect. After the redirect returns, #maybeAutofillAfterLogin consumes the flag.
   */
  startLogin(): void {
    this.#loginControlUsed.set(true);
    try {
      window.sessionStorage.setItem(AUTOFILL_INTENT_KEY, '1');
    } catch {
      // sessionStorage may be unavailable (privacy mode); ignore
    }
    void this.#auth.login();
  }

  #startUrlAutofill(): void {
    if (!this.#ctx || !this.authEnabled()) return;

    this.#intentActive = true;
    this.#loginControlUsed.set(true);

    if (this.isAuthenticated()) {
      this.#maybeAutofillAfterLogin();
      return;
    }

    try {
      window.sessionStorage.setItem(AUTOFILL_INTENT_KEY, '1');
    } catch {
      // sessionStorage may be unavailable (privacy mode); ignore
    }
    void this.#auth.login();
  }

  /**
   * Patch matching form controls with values from the Mobiliar ID profile.
   *
   * @param force when true (explicit banner click), overwrite existing values too — the
   *   user asked to (re-)apply their profile. When false (automatic post-login fill), only
   *   fill empty, non-dirty controls so URL-prepopulated, persisted and user-entered values
   *   always win.
   * @returns the number of controls actually patched.
   */
  applyProfilePrefill(force = false): number {
    if (isPlatformServer(this.#platformId)) return 0;
    if (!this.#ctx) return 0;
    const info: AuthUserInfo | null = this.#auth.userInfo();
    if (!info || !this.isAuthenticated()) return 0;

    const { webformId } = this.#ctx;
    let patched = 0;
    Object.keys(PROFILE_FIELD_MAP).forEach((key) => {
      if (this.#applyProfileValue(`${webformId}--${key}`, force)) patched++;
    });
    return patched;
  }

  /**
   * Restore one profile-mapped field after a successful form-login autofill. This is called
   * when a conditional field becomes visible again after clear-on-hide reset its control.
   */
  prefillControl(id: string, force = false): boolean {
    if (!this.#filled()) return false;
    return this.#applyProfileValue(id, force);
  }

  #applyProfileValue(id: string, force = false): boolean {
    if (isPlatformServer(this.#platformId) || !this.#ctx) return false;
    const info: AuthUserInfo | null = this.#auth.userInfo();
    if (!info || !this.isAuthenticated()) return false;

    const { getControl, getField, webformId } = this.#ctx;
    const key = id.replace(`${webformId}--`, '');
    const deriver = PROFILE_FIELD_MAP[key as keyof typeof PROFILE_FIELD_MAP];
    if (!deriver) return false;

    const value = deriver(info);
    const control = getControl(id);
    if (!value || !control || (!force && !this.#shouldOverwrite(control))) return false;

    control.setValue(this.#toControlValue(value, getField(id)));
    control.markAsTouched();
    return true;
  }

  /**
   * Auto-fill after a login that was started from the in-form banner (intent flag set
   * before the redirect). The flag is consumed once and kept active per-form so prefill
   * re-applies as the async userinfo profile enriches; the success message shows once.
   */
  #maybeAutofillAfterLogin(): void {
    if (!this.#ctx || !this.isAuthenticated()) return;

    if (!this.#intentActive && this.#consumeAutofillIntent()) {
      this.#intentActive = true;
      this.#loginControlUsed.set(true);
    }
    if (!this.#intentActive) return;

    const patched = this.applyProfilePrefill();
    if (patched === 0) return;

    this.#filled.set(true);
    if (!this.#notified) {
      this.#notified = true;
      this.#pushMessage('success');
    }
    this.#ctx.detectChanges();
  }

  #consumeAutofillIntent(): boolean {
    if (isPlatformServer(this.#platformId)) return false;
    try {
      if (window.sessionStorage.getItem(AUTOFILL_INTENT_KEY) === '1') {
        window.sessionStorage.removeItem(AUTOFILL_INTENT_KEY);
        return true;
      }
    } catch {
      // sessionStorage may be unavailable (privacy mode); ignore
    }
    return false;
  }

  #shouldOverwrite(control: AbstractControl): boolean {
    const value = control.value;
    const isEmpty =
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'object' && Object.keys(value).length === 0);
    return isEmpty && !control.dirty;
  }

  /**
   * Webforms encode the same profile value with different option keys. Resolve
   * the profile value against the actual field options before writing it to the
   * reactive control. Mobi UI checkbox groups require a key/boolean object,
   * whereas select and radio controls expect the selected option key.
   */
  #toControlValue(
    value: string | Record<string, string>,
    field: Record<string, unknown> | undefined
  ): OptionKey | Record<string, string | boolean> {
    if (
      typeof value !== 'string' ||
      !field ||
      !['select', 'radios', 'checkboxes'].includes(String(field['#type']))
    ) {
      return value;
    }

    const options = this.#getOptions(field['#options']);
    const selected = this.#getOption(value, options);
    if (!selected) return value;

    if (field['#type'] === 'checkboxes') {
      return Object.fromEntries(
        options.map(({ key }) => [String(key), key === selected.key])
      );
    }

    return selected.key;
  }

  /**
   * Keys keep their original type: radios compare option values strictly, and
   * numeric YAML keys (e.g. `6: Herr`) arrive as numbers.
   */
  #getOptions(options: unknown): FieldOption[] {
    if (Array.isArray(options)) {
      return options.flatMap((option) => {
        if (!option || typeof option !== 'object') return [];
        const { key, value } = option as Record<string, unknown>;
        return typeof key === 'string' || typeof key === 'number'
          ? [{ key, label: String(value ?? '') }]
          : [];
      });
    }

    if (!options || typeof options !== 'object') return [];
    return Object.entries(options).map(([key, label]) => ({ key, label: String(label) }));
  }

  #getOption(value: string, options: FieldOption[]): FieldOption | undefined {
    const normalizedValue = value.trim().toLocaleLowerCase();
    const salutationLabel = { '6': 'herr', '7': 'frau' }[normalizedValue];
    const expectedValue = salutationLabel || normalizedValue;

    return options.find(({ key, label }) => {
      const normalizedKey = String(key).trim().toLocaleLowerCase();
      const normalizedLabel = label.split(' -- ')[0].trim().toLocaleLowerCase();
      return normalizedKey === expectedValue || normalizedLabel === expectedValue;
    });
  }

  /**
   * Push a localized autofill success/error notification into the host's message list
   * (rendered by <mobi-ui-messages-major>). The text fetch is async, so change detection
   * is triggered inside the promise callbacks once the message is appended.
   */
  #pushMessage(kind: 'success' | 'error'): void {
    if (!this.#ctx) return;
    const ctx = this.#ctx;
    const fallback =
      kind === 'success'
        ? 'Ihre Angaben wurden aus Ihrer Mobiliar ID übernommen.'
        : 'Ihre Angaben konnten nicht übernommen werden. Bitte versuchen Sie es erneut.';

    this.#texts
      .getText(`form_autofill_${kind}`, 'frontend', fallback)
      .then((text: string) => {
        ctx.addMessage({
          id: `autofill-${kind}-${new Date().getTime()}`,
          level: kind === 'success' ? 'success' : 'warning',
          text: text || fallback,
          messageCloseTimeout: AUTOFILL_MESSAGE_CLOSE_TIMEOUT_MS,
        });
        ctx.detectChanges();
      })
      .catch(() => {
        ctx.addMessage({
          id: `autofill-${kind}-${new Date().getTime()}`,
          level: kind === 'success' ? 'success' : 'warning',
          text: fallback,
          messageCloseTimeout: AUTOFILL_MESSAGE_CLOSE_TIMEOUT_MS,
        });
        ctx.detectChanges();
      });
  }
}
